/**
 * Widget Library Component
 * Menu to show/hide widgets on the dashboard
 */

import React from "react";
import { LayoutGrid, Eye, EyeOff } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { DashboardLayout, WidgetId } from "../types";
import { ALL_WIDGET_IDS } from "../utils/defaultLayout";
import { getWidgetMetadata } from "../utils/widgetRegistry";

interface WidgetLibraryProps {
  layout: DashboardLayout;
  onToggleWidget: (widgetId: WidgetId) => void;
}

export function WidgetLibrary({ layout, onToggleWidget }: WidgetLibraryProps) {
  // Get visible widget IDs
  const visibleWidgetIds = new Set(
    layout.widgets.filter((w) => w.visible).map((w) => w.widgetId)
  );

  // Group widgets by category
  const widgetsByCategory = {
    core: ALL_WIDGET_IDS.filter(
      (id) => getWidgetMetadata(id)?.category === "core"
    ),
    insights: ALL_WIDGET_IDS.filter(
      (id) => getWidgetMetadata(id)?.category === "insights"
    ),
  };

  const visibleCount = visibleWidgetIds.size;
  const totalCount = ALL_WIDGET_IDS.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <LayoutGrid className="h-4 w-4" />
          Widgets
          <Badge variant="secondary" className="ml-1 text-xs">
            {visibleCount}/{totalCount}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Show/Hide Widgets</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Core Analytics Widgets */}
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2 py-1.5">
          Core Analytics
        </DropdownMenuLabel>
        {widgetsByCategory.core.map((widgetId) => {
          const metadata = getWidgetMetadata(widgetId);
          const isVisible = visibleWidgetIds.has(widgetId);
          return (
            <DropdownMenuItem
              key={widgetId}
              onClick={() => onToggleWidget(widgetId)}
              className={`cursor-pointer flex items-center justify-between hover:bg-accent hover:text-accent-foreground ${
                isVisible ? "bg-accent/50" : ""
              }`}
              onSelect={(e) => e.preventDefault()}
            >
              <span className="flex-1">{metadata?.title || widgetId}</span>
              {isVisible ? (
                <Eye className="h-4 w-4 text-accent" />
              ) : (
                <EyeOff className="h-4 w-4 text-muted-foreground opacity-50" />
              )}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />

        {/* Additional Insights Widgets */}
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2 py-1.5">
          Additional Insights
        </DropdownMenuLabel>
        {widgetsByCategory.insights.map((widgetId) => {
          const metadata = getWidgetMetadata(widgetId);
          const isVisible = visibleWidgetIds.has(widgetId);
          return (
            <DropdownMenuItem
              key={widgetId}
              onClick={() => onToggleWidget(widgetId)}
              className={`cursor-pointer flex items-center justify-between hover:bg-accent hover:text-accent-foreground ${
                isVisible ? "bg-accent/50" : ""
              }`}
              onSelect={(e) => e.preventDefault()}
            >
              <span className="flex-1">{metadata?.title || widgetId}</span>
              {isVisible ? (
                <Eye className="h-4 w-4 text-accent" />
              ) : (
                <EyeOff className="h-4 w-4 text-muted-foreground opacity-50" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
